import { List, Map } from 'immutable'
import { createSelector } from 'reselect'

import {
  Appointment,
  CONVERSATION_STATE,
  CONVERSATION_TYPE,
  ConversationObject,
  Episode,
  EpisodeStates,
  ImmutableTypedMap,
  Provider,
  ProviderInfo,
} from '@lyrahealth-inc/shared-app-logic'

import { getOnboardProviderProfileData } from '../../features/onboard/data/onboardSelectors'

const getImmutableLyraTherapyState = (state: Map<string, any>) => state?.get('lyraTherapy')
const getLyraTherapyState = createSelector(getImmutableLyraTherapyState, (immutableLyraTherapyState) => {
  return immutableLyraTherapyState?.toJS()
})
export const getAssignmentData = createSelector(getLyraTherapyState, (lyraTherapyState) => {
  return lyraTherapyState?.assignments?.data
})
export const getAssignmentSelectedIndex = createSelector(getLyraTherapyState, (lyraTherapyState) => {
  return lyraTherapyState?.assignments?.selectedIndex
})
export const getSelectedAssignment = createSelector(
  [getAssignmentData, getAssignmentSelectedIndex],
  (assignmentData, assignmentSelectedIndex) => {
    return assignmentData[assignmentSelectedIndex]
  },
)
export const getLyraTherapyEpisodes = createSelector(
  getLyraTherapyState,
  (lyraTherapyState) => lyraTherapyState.episodes,
)

export const getLyraTherapy = (state: Map<string, any>) => state?.get('lyraTherapy')

export const getEpisodes = createSelector(getLyraTherapy, (lyraTherapy) => lyraTherapy?.get('episodes', Map()))

export const getOutcomes = createSelector(getLyraTherapy, (lyraTherapy) => lyraTherapy?.get('outcomes', Map()))

export const getOutcomesData = createSelector(getOutcomes, (outcomes) => outcomes?.get('data', List()))

export const getSessions = createSelector(getLyraTherapy, (lyraTherapy) => lyraTherapy?.get('sessions'))

export const getPrograms = createSelector(getLyraTherapy, (lyraTherapy) => lyraTherapy?.get('programs') ?? List())

export const getMessenger = createSelector(getLyraTherapy, (lyraTherapy) => lyraTherapy?.get('messenger'))

export const getSelectedProviderId = createSelector(getMessenger, (messenger) => messenger?.get('selectedProviderId'))

export const getSelectedConversationId = createSelector(getMessenger, (messenger) =>
  messenger?.get('selectedConversationId'),
)

export const getConversations = createSelector(getMessenger, (messenger) => messenger?.get('conversations') ?? List())

export const getAICoachConversations = createSelector(getConversations, (conversations) =>
  conversations.filter(
    (conversation: List<ConversationObject>) =>
      conversation?.getIn(['conversation_attributes', 'type']) === CONVERSATION_TYPE.AI_COACH &&
      ![CONVERSATION_STATE.CLOSED, CONVERSATION_STATE.PENDING].includes(conversation?.getIn(['conversation_state'])),
  ),
)

export const getAICoachIdFromConversation = createSelector(getAICoachConversations, (aiConversations) =>
  aiConversations.find((c: any) => c.get('provider_lyra_id') != null)?.get('provider_lyra_id'),
)

export const getAICoachConversationId = createSelector(getAICoachConversations, (aiConversations) =>
  aiConversations.find((c: any) => c.get('conversation_id') != null)?.get('conversation_id'),
)

export const getMessengerDrafts = createSelector(getMessenger, (messenger) => messenger?.get('drafts') ?? List())

export const getShowMessengerPopover = createSelector(getMessenger, (messenger) => messenger?.get('showPopover'))

export const getAwaitingAiResponse = createSelector(
  getMessenger,
  (messenger) => messenger?.get('awaitingAiResponse') ?? false,
)

export const getMessages = createSelector(getMessenger, (messenger) => messenger?.get('messages', Map()))

export const getConversationsClient = createSelector(getMessenger, (messenger) => messenger?.get('conversationsClient'))

export const getInLiveMsgSession = createSelector(getMessenger, (messenger) => messenger?.get('inLiveMsgSession'))

export const getActiveLiveMsgAppt = createSelector(getMessenger, (messenger) => messenger?.get('activeLiveMsgAppt'))

export const getLiveMsgProviderId = createSelector(getActiveLiveMsgAppt, (activeLiveMsgAppt) =>
  activeLiveMsgAppt?.getIn(['provider', 'lyra_id'], ''),
)

export const getLiveMsgProviderIdToOpen = createSelector(getMessenger, (messenger) =>
  messenger?.get('liveMsgProviderIdToOpen'),
)

export const getShownLiveMsgModal = createSelector(getMessenger, (messenger) => messenger?.get('shownModal'))

export const getAppointment = createSelector(getSessions, (sessions) => sessions?.get('appointment'))

export const getAppointmentReschedule = createSelector(getSessions, (sessions) =>
  sessions?.get('appointmentReschedule'),
)

export const getSessionEpisodes = createSelector(getSessions, (sessions) => sessions?.get('episodes'))

export const getCurrentEpisodes = createSelector(getSessionEpisodes, (sessionEpisodes) =>
  sessionEpisodes?.get('current'),
)

export const getProviders = createSelector(getLyraTherapy, (lyraTherapy) => lyraTherapy?.get('providers'))

export const getSelectedProvider = createSelector([getProviders, getSelectedProviderId], (providers, id) => {
  return providers?.find((provider: Map<string, any>) => provider.getIn(['provider', 'lyra_id']) === id) ?? Map()
})

export const getSelectedConversation = createSelector(
  [getConversations, getSelectedProvider],
  (conversations: ImmutableTypedMap<ConversationObject>[], provider) => {
    return conversations?.find((conversation) => conversation?.get('provider_lyra_id') === provider.get('lyra_id'))
  },
)

export const getCurrentProvider = createSelector(
  [getProviders, getOnboardProviderProfileData],
  (providers: ImmutableTypedMap<Provider>[], onboardProviderProfileData: ImmutableTypedMap<ProviderInfo>) => {
    return providers?.find(
      (provider) =>
        (provider?.get('provider') as ImmutableTypedMap<ProviderInfo>)?.get('lyra_id') ===
        onboardProviderProfileData?.get('lyra_id'),
    )
  },
)

// @ts-expect-error TS(2769): No overload matches this call.
export const getCurrentProviderAppointments = createSelector(
  getCurrentProvider,
  (provider: ImmutableTypedMap<Provider>) => provider?.get('appointments'),
)

export const getCurrentProviderActiveEpisodeAppointments = createSelector(
  [getCurrentProviderAppointments, getEpisodes],
  (appointments, episodes) => {
    const typedAppts: List<ImmutableTypedMap<Appointment>> = appointments as unknown as List<
      ImmutableTypedMap<Appointment>
    >
    const currentEpisodeIds = (episodes?.toJS() as Episode[])
      ?.filter((episode) => episode.state === EpisodeStates.IN_PROGRESS)
      .map((episode) => episode.id)
    return typedAppts
      ?.toJS()
      ?.filter(
        (appointment: Appointment) => appointment.episodeId && currentEpisodeIds?.includes(appointment.episodeId),
      )
  },
)

export const getCurrentAppointmentProvider = createSelector(
  [getProviders, getAppointment],
  (providers: ImmutableTypedMap<Provider>[], appoinment: ImmutableTypedMap<Appointment>) =>
    providers?.find(
      (provider) =>
        (provider?.get('provider') as ImmutableTypedMap<ProviderInfo>)?.get('lyra_id') ===
        (appoinment?.get('provider') as ImmutableTypedMap<ProviderInfo>)?.get('lyra_id'),
    ),
)

export const getCurrentAppointmentProviderInfo = createSelector(
  getCurrentAppointmentProvider,
  (provider: ImmutableTypedMap<Provider> | undefined) => provider?.get('provider'),
)

export const getCurrentAppointmentProviderActiveEpisodeAppointments = createSelector(
  [getCurrentAppointmentProvider, getEpisodes],
  (provider: ImmutableTypedMap<Provider> | undefined, episodes) => {
    const currentEpisodeIds = (episodes?.toJS() as Episode[])
      ?.filter((episode) => episode.state === EpisodeStates.IN_PROGRESS)
      .map((episode) => episode.id)
    return (
      (provider?.get('appointments') as List<Appointment>)
        ?.toJS()
        ?.filter(
          (appointment: Appointment) => appointment.episodeId && currentEpisodeIds?.includes(appointment.episodeId),
        ) ?? []
    )
  },
)
